import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SERVICES, IMG } from "@/lib/content";
import CurrencyStrip from "@/components/CurrencyStrip";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/* ----------------------------- Mega-menu data --------------------------- */

const SERVICES_GROUPS = [
  {
    title: "Sourcing",
    items: [
      { slug: "furniture-sourcing",      label: "Furniture Sourcing" },
      { slug: "hotel-ffe",                label: "Hotel & FF&E Procurement" },
      { slug: "building-materials",       label: "Building Materials" },
      { slug: "machinery-sourcing",       label: "Machinery & Mining" },
      { slug: "private-label",            label: "Private Label Sourcing" },
      { slug: "oem-odm",                  label: "OEM / ODM" },
    ],
  },
  {
    title: "Quality & Risk",
    items: [
      { slug: "supplier-verification",    label: "Supplier Verification" },
      { slug: "factory-audit",            label: "Factory Audits & QC" },
      { slug: "quality-inspection",       label: "Pre-shipment Inspection" },
      { slug: "import-compliance",        label: "Import Compliance Advisory" },
    ],
  },
  {
    title: "Freight & Documentation",
    items: [
      { slug: "export-logistics",         label: "Export Logistics" },
      { slug: "freight-forwarding",       label: "Freight Forwarding" },
      { slug: "container-consolidation",  label: "Container Consolidation" },
      { slug: "warehousing",              label: "Warehousing & Distribution" },
      { slug: "export-documentation",     label: "Export Documentation" },
    ],
  },
];

const SOLUTIONS_LINKS = [
  { label: "By Industry, Real Estate Developers",   to: "/services/building-materials" },
  { label: "By Industry, Hotel Groups",             to: "/services/hotel-ffe" },
  { label: "By Industry, Furniture Importers",      to: "/services/furniture-sourcing" },
  { label: "By Industry, Industrial / Machinery",   to: "/services/machinery-sourcing" },
  { label: "By Industry, Private-Label Brands",     to: "/services/private-label" },
  { label: "By Logistics, DDP Shipping from China", to: "/ddp" },
  { label: "By Geography, China Manufacturing Map", to: "/clusters" },
  { label: "By Geography, Country Compliance",      to: "/import-from-china/india" },
];

const RESOURCES_LINKS = [
  { label: "Calculators & Tools",                       to: "/tools" },
  { label: "Free Excel Templates (currency-aware)",     to: "/tools/downloads" },
  { label: "Cluster Intelligence Reports (gated PDF)",  to: "/clusters" },
  { label: "Insights & Long-Form Articles",             to: "/insights" },
  { label: "Trade Glossary",                            to: "/glossary" },
  { label: "Country Compliance Library",                to: "/import-from-china/india" },
  { label: "FAQ",                                       to: "/faq" },
];

const COMPANY_LINKS = [
  { label: "About",            to: "/about" },
  { label: "Why Fortune",      to: "/why-fortune" },
  { label: "The Fortune Process", to: "/process" },
  { label: "China Business Trips",to: "/china-business-trips" },
  { label: "Careers",          to: "/careers" },
  { label: "Press & Trust",    to: "/about#press" },
  { label: "Contact",          to: "/contact" },
];

/* ----------------------------- Header --------------------------------- */

export default function Header() {
  const [openMobile, setOpenMobile] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'services' | 'solutions' | 'resources' | 'company'
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpenMobile(false); setOpenMenu(null); }, [loc.pathname]);

  const portalPath = user?.role === "supplier" ? "/portal/vendor"
    : ["admin", "sales", "management", "procurement"].includes(user?.role) ? "/portal/admin"
    : "/portal/client";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#1C3E5F] ${
        scrolled ? "border-b border-[#B8965A]/20 shadow-lg" : "border-b border-transparent"
      }`}
      onMouseLeave={() => setOpenMenu(null)}
    >
      {/* Utility strip: language switcher (visible on ALL screen sizes) */}
      <div className="bg-[#08192A] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 flex items-center justify-end py-1 gap-3 text-white/60 text-[10px] uppercase tracking-[0.14em]">
          <span className="hidden sm:inline">Global site · translate:</span>
          <LanguageSwitcher />
        </div>
      </div>

      <CurrencyStrip />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          <img src="/fortune-logo-white.png" alt="Fortune Sourcings" className="h-11 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-2" data-testid="primary-nav">
          <MegaItem
            label="Services"
            id="services"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          >
            <ServicesMega />
          </MegaItem>
          <MegaItem
            label="Solutions"
            id="solutions"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          >
            <SimpleMega
              title="Solutions for serious importers"
              items={SOLUTIONS_LINKS}
              cta={{ label: "Submit RFQ", to: "/submit-rfq", desc: "Get a senior sourcing lead on your project within one working day." }}
            />
          </MegaItem>
          <Link
            to="/clusters"
            data-testid="nav-clusters"
            className="link-underline text-[12px] uppercase tracking-[0.16em] text-white/85 hover:text-[#B8965A] font-medium px-3"
          >
            Clusters
          </Link>
          <Link
            to="/china-business-trips"
            data-testid="nav-china-trips"
            className="link-underline text-[12px] uppercase tracking-[0.16em] text-white/85 hover:text-[#B8965A] font-medium px-3"
          >
            China Trips
          </Link>
          <MegaItem
            label="Resources"
            id="resources"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          >
            <SimpleMega
              title="Resources & intelligence"
              items={RESOURCES_LINKS}
              cta={{ label: "Download Company Profile", to: "/company-profile.pdf", external: true,
                     desc: "Auto-generated PDF, services, categories, on-ground footprint." }}
            />
          </MegaItem>
          <MegaItem
            label="Company"
            id="company"
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          >
            <SimpleMega
              title="Inside Fortune Sourcings"
              items={COMPANY_LINKS}
              cta={{ label: "Book Consultation", to: "/book-consultation",
                     desc: "30-minute call with a senior sourcing lead." }}
            />
          </MegaItem>
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {user ? (
            <Link to={portalPath} data-testid="portal-cta" className="btn-outline !py-2.5 !px-5 !text-[11px] whitespace-nowrap">Portal</Link>
          ) : (
            <div className="relative" onMouseEnter={() => setOpenMenu("login")}>
              <button
                data-testid="login-cta"
                className={`text-[11px] uppercase tracking-[0.16em] font-medium px-2 inline-flex items-center gap-1.5 ${
                  openMenu === "login" ? "text-[#B8965A]" : "text-white/80 hover:text-[#B8965A]"
                }`}
              >
                Login
                <ChevronDown size={11} className={`transition-transform ${openMenu === "login" ? "rotate-180" : ""}`} />
              </button>
              {openMenu === "login" && (
                <div className="absolute right-0 top-full pt-3" data-testid="login-mega">
                  <div className="bg-white shadow-2xl border-t-2 border-[#B8965A] min-w-[240px] py-1">
                    <Link to="/login?as=team" className="flex flex-col px-4 py-2.5 hover:bg-[#F8FAFC] hover:text-[#B8965A]" data-testid="login-team">
                      <span className="text-[12px] font-semibold text-[#1C3E5F]">Team Login</span>
                      <span className="text-[10px] text-[#1C3E5F]/55">Sales · Procurement · Admin · Management</span>
                    </Link>
                    <Link to="/login?as=client" className="flex flex-col px-4 py-2.5 hover:bg-[#F8FAFC] hover:text-[#B8965A] border-t border-[#E2E8F0]" data-testid="login-client">
                      <span className="text-[12px] font-semibold text-[#1C3E5F]">Client Login</span>
                      <span className="text-[10px] text-[#1C3E5F]/55">Importers and buyers</span>
                    </Link>
                    <Link to="/login?as=vendor" className="flex flex-col px-4 py-2.5 hover:bg-[#F8FAFC] hover:text-[#B8965A] border-t border-[#E2E8F0]" data-testid="login-vendor">
                      <span className="text-[12px] font-semibold text-[#1C3E5F]">Vendor Login</span>
                      <span className="text-[10px] text-[#1C3E5F]/55">Suppliers and factories</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
          <Link to="/submit-rfq" data-testid="header-rfq-cta" className="btn-primary !py-2.5 !px-5 !text-[11px] whitespace-nowrap">Submit RFQ</Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpenMobile(!openMobile)}
          aria-label="Menu"
        >
          {openMobile ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      {openMobile && (
        <div className="lg:hidden bg-[#1C3E5F] border-t border-[#B8965A]/20 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-3">
            <MobileGroup label="Services" items={SERVICES_GROUPS.flatMap(g => g.items.map(i => ({ label: i.label, to: `/services/${i.slug}` })))} />
            <MobileGroup label="Solutions" items={SOLUTIONS_LINKS} />
            <Link to="/clusters" className="text-white/90 text-sm uppercase tracking-[0.18em] py-1">Clusters</Link>
            <Link to="/china-business-trips" className="text-white/90 text-sm uppercase tracking-[0.18em] py-1">China Trips</Link>
            <MobileGroup label="Resources" items={RESOURCES_LINKS} />
            <MobileGroup label="Company" items={COMPANY_LINKS} />
            <div className="pt-4 mt-3 border-t border-white/10 flex flex-col gap-3">
              {user ? <Link to={portalPath} className="btn-outline">Portal</Link> : (
                <div className="grid grid-cols-3 gap-2">
                  <Link to="/login?as=team"   className="btn-outline !py-2 !px-2 !text-[10px] text-center">Team</Link>
                  <Link to="/login?as=client" className="btn-outline !py-2 !px-2 !text-[10px] text-center">Client</Link>
                  <Link to="/login?as=vendor" className="btn-outline !py-2 !px-2 !text-[10px] text-center">Vendor</Link>
                </div>
              )}
              <Link to="/submit-rfq" className="btn-primary">Submit RFQ</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ----------------------------- Subparts -------------------------------- */

function MegaItem({ label, id, openMenu, setOpenMenu, children }) {
  const open = openMenu === id;
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenMenu(id)}
    >
      <button
        data-testid={`nav-${label.toLowerCase()}`}
        className={`link-underline text-[12px] uppercase tracking-[0.16em] font-medium px-3 inline-flex items-center gap-1.5 ${
          open ? "text-[#B8965A]" : "text-white/85 hover:text-[#B8965A]"
        }`}
        aria-expanded={open}
      >
        {label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
          data-testid={`mega-${label.toLowerCase()}`}
        >
          <div className="bg-white shadow-2xl border-t-2 border-[#B8965A] min-w-[680px] animate-fadeIn">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesMega() {
  // Find the hero card image, use the first service image
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_280px]">
      {SERVICES_GROUPS.map((g) => (
        <div key={g.title} className="p-6 border-r border-[#E2E8F0]">
          <div className="eyebrow text-[#B8965A] mb-3">{g.title}</div>
          <ul className="space-y-2">
            {g.items.map((it) => {
              const svc = SERVICES.find(s => s.slug === it.slug);
              return (
                <li key={it.slug}>
                  <Link
                    to={`/services/${it.slug}`}
                    className="group block py-1.5"
                    data-testid={`mega-service-${it.slug}`}
                  >
                    <div className="text-[13px] text-[#1C3E5F] font-semibold group-hover:text-[#B8965A] transition-colors leading-tight">
                      {it.label}
                    </div>
                    {svc && (
                      <div className="text-[11px] text-[#1C3E5F]/55 mt-0.5 line-clamp-1">
                        {svc.tag}
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <div className="bg-[#1C3E5F] text-white p-6 flex flex-col justify-between">
        <img src={IMG.factory} alt="Factory floor" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover mb-4 opacity-90" />
        <div>
          <div className="eyebrow text-[#B8965A] mb-2">Featured</div>
          <div className="font-serif text-xl leading-tight mb-2">Tell us your sourcing brief.</div>
          <p className="text-[12px] text-white/65 mb-4 leading-relaxed">A senior sourcing lead responds within one working day. Container-load and project-based engagements only.</p>
          <Link to="/submit-rfq" className="inline-flex items-center gap-1.5 text-[#B8965A] text-[11px] uppercase tracking-[0.16em] font-semibold hover:underline">
            Submit RFQ <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SimpleMega({ title, items, cta }) {
  return (
    <div className="grid grid-cols-[1fr_280px]">
      <div className="p-6">
        <div className="eyebrow text-[#B8965A] mb-4">{title}</div>
        <ul className="grid grid-cols-1 gap-1.5">
          {items.map((it, i) => (
            <li key={i}>
              <Link
                to={it.to}
                className="block py-2 text-[13px] text-[#1C3E5F] hover:text-[#B8965A] hover:bg-[#F8FAFC] px-3 -mx-3 rounded-sm font-medium leading-tight"
                data-testid={`mega-link-${i}`}
              >
                {it.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-[#1C3E5F] text-white p-6 flex flex-col justify-end">
        <div className="eyebrow text-[#B8965A] mb-2">Featured</div>
        <div className="font-serif text-lg leading-tight mb-2">{cta.label}</div>
        <p className="text-[12px] text-white/65 mb-4 leading-relaxed">{cta.desc}</p>
        {cta.external ? (
          <a href={cta.to} className="inline-flex items-center gap-1.5 text-[#B8965A] text-[11px] uppercase tracking-[0.16em] font-semibold hover:underline">
            {cta.label} <ArrowUpRight size={12} />
          </a>
        ) : (
          <Link to={cta.to} className="inline-flex items-center gap-1.5 text-[#B8965A] text-[11px] uppercase tracking-[0.16em] font-semibold hover:underline">
            {cta.label} <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}

function MobileGroup({ label, items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 pb-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-white/90 text-sm uppercase tracking-[0.18em] py-2"
        data-testid={`mobile-group-${label.toLowerCase()}`}
      >
        {label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pt-2 pl-2 flex flex-col gap-2 border-l border-[#B8965A]/30 ml-1">
          {items.map((it, i) => (
            <Link
              key={i}
              to={it.to}
              className="text-[12px] text-white/75 hover:text-[#B8965A]"
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
